import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useMeta } from '../contexts/MetaContext';

const ProductImageGallery = ({ product }) => {
  const { images, id, meta } = product;
  const { metaData } = useMeta();
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  
  // Determine the number of images based on metaData.ic or fallback to images array length
  const imageCount = meta?.ic || (images ? images.length : 1);
  const displayedThumbnails = Array.from({ length: Math.min(imageCount, 7) }, (_, index) => index);
  
  // Generate image URL using CDN (index starts from 1)
  const getImageUrl = (index) => {
    return `${metaData.imageCdn}/img/product/${id}/${index + 1}.jpg`;
  };

  return (
    <Box>
      {/* Desktop layout: thumbnails on left, main image on right */}
      <Grid container spacing={{ xs: 0, md: 2 }} sx={{ display: { xs: 'none', md: 'flex' } }}>
        {/* Thumbnails column */}
        <Grid item xs={2}>
          <Grid container direction="column" spacing={1}>
            {displayedThumbnails.map((index) => (
              <Grid item key={index}>
                <Box
                  component="img"
                  src={getImageUrl(index)}
                  alt={`Product thumbnail ${index + 1}`}
                  onClick={() => setSelectedThumbnailIndex(index)}
                  sx={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    opacity: index === selectedThumbnailIndex ? 1 : 0.7,
                    transition: 'opacity 0.3s',
                    '&:hover': { opacity: 1 },
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `data:image/svg+xml;base64,${btoa(`
                      <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg">
                        <rect width="60" height="60" fill="#f0f0f0"/>
                        <text x="30" y="35" text-anchor="middle" font-family="Arial" font-size="12" fill="#999">
                          ${index + 1}
                        </text>
                      </svg>
                    `)}`;
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Main image column */}
        <Grid item xs={10}>
          <Box
            component="img"
            src={getImageUrl(selectedThumbnailIndex)}
            alt={`Selected product image ${selectedThumbnailIndex + 1}`}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '500px',
              objectFit: 'contain',
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `data:image/svg+xml;base64,${btoa(`
                <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
                  <rect width="400" height="400" fill="#f0f0f0"/>
                  <text x="200" y="200" text-anchor="middle" font-family="Arial" font-size="24" fill="#999">
                    Image ${selectedThumbnailIndex + 1}
                  </text>
                </svg>
              `)}`;
            }}
          />
        </Grid>
      </Grid>

      {/* Mobile layout: main image on top, thumbnails below */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {/* Main image */}
        <Box
          component="img"
          src={getImageUrl(selectedThumbnailIndex)}
          alt={`Selected product image ${selectedThumbnailIndex + 1}`}
          sx={{
            width: '100%',
            height: 'auto',
            maxHeight: '400px',
            objectFit: 'contain',
            mb: 2,
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `data:image/svg+xml;base64,${btoa(`
              <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
                <rect width="400" height="400" fill="#f0f0f0"/>
                <text x="200" y="200" text-anchor="middle" font-family="Arial" font-size="24" fill="#999">
                  Image ${selectedThumbnailIndex + 1}
                </text>
              </svg>
            `)}`;
          }}
        />
        
        {/* Thumbnails row */}
        <Grid container spacing={1} justifyContent="center">
          {displayedThumbnails.map((index) => (
            <Grid item key={index}>
              <Box
                component="img"
                src={getImageUrl(index)}
                alt={`Product thumbnail ${index + 1}`}
                onClick={() => setSelectedThumbnailIndex(index)}
                sx={{
                  width: '50px',
                  height: '50px',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  opacity: index === selectedThumbnailIndex ? 1 : 0.7,
                  transition: 'opacity 0.3s',
                  '&:hover': { opacity: 1 },
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `data:image/svg+xml;base64,${btoa(`
                    <svg width="50" height="50" xmlns="http://www.w3.org/2000/svg">
                      <rect width="50" height="50" fill="#f0f0f0"/>
                      <text x="25" y="30" text-anchor="middle" font-family="Arial" font-size="10" fill="#999">
                        ${index + 1}
                      </text>
                    </svg>
                  `)}`;
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ProductImageGallery;